import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Mail, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get("plan");
  
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [countdown, setCountdown] = useState(0);
  
  const inputRefs = useRef([]);

  useEffect(() => {
    // Check if user is logged in and get their email
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/register");
      return;
    }
    
    // Fetch user info
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // If already verified, redirect to Stripe checkout or pricing
        if (response.data.email_verified) {
          if (selectedPlan && selectedPlan !== "trial") {
            // Redirect to Stripe checkout
            try {
              const stripeResponse = await axios.post(
                `${API}/api/payments/create-checkout?plan_id=${selectedPlan}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (stripeResponse.data?.url) {
                window.location.href = stripeResponse.data.url;
                return;
              }
            } catch (e) {
              console.error("Stripe error:", e);
            }
          }
          // If pending_payment, go to pricing; otherwise dashboard
          if (response.data.subscription_status === "pending_payment") {
            navigate("/precios");
          } else {
            navigate("/admin/dashboard");
          }
          return;
        }
        
        setUserEmail(response.data.email);
      } catch (err) {
        console.error("Auth error:", err);
        localStorage.removeItem("token");
        navigate("/register");
      }
    };
    
    fetchUser();
  }, [navigate, selectedPlan]);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      setError("Ingresa el código completo de 6 dígitos");
      return;
    }
    
    setLoading(true);
    setError("");
    
    // Generate unique event_id for deduplication
    const eventId = `reg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/auth/verify-email`,
        { code: fullCode, event_id: eventId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(true);
      toast.success("¡Email verificado!");
      
      // Fire Facebook Pixel CompleteRegistration event with event_id for deduplication
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_name: selectedPlan || 'trial',
          status: 'verified'
        }, { eventID: eventId });
        console.log('Meta Pixel: CompleteRegistration event fired with eventID:', eventId);
      }
      
      // Wait a moment then redirect
      setTimeout(async () => {
        if (selectedPlan && selectedPlan !== "trial") {
          // Redirect to Stripe checkout directly
          try {
            const stripeResponse = await axios.post(
              `${API}/api/payments/create-checkout?plan_id=${selectedPlan}`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (stripeResponse.data?.url) {
              window.location.href = stripeResponse.data.url;
              return;
            }
          } catch (e) {
            console.error("Stripe error:", e);
            toast.error("Error al conectar con Stripe. Ve a elegir tu plan.");
          }
        }
        // Redirect to pricing page so user can select a plan
        navigate("/precios");
      }, 1500);
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Código incorrecto";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/auth/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Código reenviado a tu correo");
      setCountdown(60); // 60 second cooldown
      setCode(["", "", "", "", "", ""]); // Clear current code
      inputRefs.current[0]?.focus();
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al reenviar código";
      toast.error(errorMsg);
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Email Verificado!</h2>
            <p className="text-gray-600">Redirigiendo...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center">
            <Link to="/" className="flex justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/d99lgtpt_inmopages-fondoblanco.png" 
                alt="InmoPages" 
                className="h-12"
              />
            </Link>
            <div className="w-16 h-16 bg-brand-purple/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-brand-purple" />
            </div>
            <CardTitle className="text-2xl">Verifica tu Email</CardTitle>
            <CardDescription className="mt-2">
              Enviamos un código de 6 dígitos a
              <br />
              <span className="font-semibold text-gray-800">{userEmail}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Code input */}
              <div className="flex justify-center gap-2" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-brand-purple focus:ring-brand-purple"
                    data-testid={`code-input-${index}`}
                  />
                ))}
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              <Button
                onClick={handleVerify}
                className="w-full bg-brand-purple hover:bg-brand-pink text-white h-12 text-base font-semibold"
                disabled={loading || code.join("").length !== 6}
                data-testid="verify-button"
              >
                {loading ? "Verificando..." : "Verificar Email"}
              </Button>
              
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">¿No recibiste el código?</p>
                <Button
                  variant="ghost"
                  onClick={handleResendCode}
                  disabled={resending || countdown > 0}
                  className="text-brand-purple hover:text-brand-pink"
                  data-testid="resend-button"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${resending ? "animate-spin" : ""}`} />
                  {countdown > 0 ? `Reenviar en ${countdown}s` : "Reenviar código"}
                </Button>
              </div>
              
              <div className="text-center pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Revisa tu bandeja de spam si no ves el correo.
                  <br />
                  El código expira en 15 minutos.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
